import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, ScrollText, Timer, Workflow, XCircle } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDateTime as stamp } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Card from "../../components/ui/Card";
import EmptyState from "../../components/ui/EmptyState";
import JsonBlock from "../../components/ui/JsonBlock";
import ListFilters from "../../components/ui/ListFilters";
import Pagination from "../../components/ui/Pagination";
import Skeleton from "../../components/ui/Skeleton";
import Stat from "../../components/ui/Stat";
import { useToast } from "../../components/ui/Toast";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";
import { ago, CopyId, Drawer, DrawerLoading, InfoGrid, Pill, Section, useOpenParam } from "../../components/directory/shared";

/**
 * Every provider call made for a customer: messages sent from the inbox,
 * replies arriving from the provider, and workflow action steps.
 *
 * Read-only. Rows are scrubbed of secrets when they're written (see backend
 * _utils/activityLog.js) and expire after 30 days.
 */

const DEFAULTS = { page: 1, limit: 50, appId: "", kind: "", result: "", direction: "", since: "", q: "" };

const what = (r) => (r.kind === "action" ? r.action || "Action" : r.direction === "inbound" ? "Message received" : "Message sent");

const ResultPill = ({ row }) =>
  row.success ? (
    <Pill tone="emerald">OK</Pill>
  ) : row.level === "warning" ? (
    <Pill tone="amber" icon={AlertTriangle}>Warning</Pill>
  ) : (
    <Pill tone="rose" icon={XCircle}>Failed</Pill>
  );

const KindPill = ({ row }) =>
  row.kind === "action" ? (
    <Pill tone="violet" icon={Workflow}>Action</Pill>
  ) : row.direction === "inbound" ? (
    <Pill tone="brand" icon={ArrowDownLeft}>Inbound</Pill>
  ) : (
    <Pill tone="ink" icon={ArrowUpRight}>Outbound</Pill>
  );

const ActivityDrawer = ({ id, onClose }) => {
  const { logs } = useAppServices();
  const [row, setRow] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setRow(null);
    setError("");
    logs.ActivityGet(id).then(({ response, error: err }) => {
      if (!alive) return;
      if (err || !response) setError(err?.message || "Could not load this entry.");
      else setRow(response);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Drawer
      onClose={onClose}
      label="Log entry"
      header={
        row ? (
          <>
            <h2 className="truncate text-[16px] font-semibold tracking-tight text-ink-900">{what(row)}</h2>
            <p className="truncate text-[12.5px] text-ink-500">{row.appName}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <ResultPill row={row} />
              <KindPill row={row} />
              {row.status && <Pill tone="ink">HTTP {row.status}</Pill>}
            </div>
          </>
        ) : (
          <div className="space-y-2 pt-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
        )
      }
    >
      {error ? (
        <p className="m-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
      ) : !row ? (
        <DrawerLoading />
      ) : (
        <>
          <InfoGrid
            rows={[
              ["When", stamp(row.createdAt)],
              ["Took", row.durationMs != null && `${row.durationMs.toLocaleString()} ms`],
              ["Account", row.accountName],
              ["Location ID", row.locationId && <CopyId value={row.locationId} />],
              ["Company ID", row.companyId && <CopyId value={row.companyId} />],
              ["Provider", row.provider],
              ["Install ID", row.installId && <CopyId value={row.installId} />],
              ["Expires", row.expiresAt && stamp(row.expiresAt)],
            ]}
          />
          {row.message && (
            <Section title="Message" icon={ScrollText}>
              <p className={cx("rounded-xl border bg-white px-3.5 py-2.5 text-[13px] leading-relaxed", row.success ? "border-ink-200/70 text-ink-700" : row.level === "warning" ? "border-amber-200 text-amber-800" : "border-rose-200 text-rose-700")}>
                {row.message}
              </p>
            </Section>
          )}
          <Section title="Payloads" icon={ScrollText}>
            <div className="space-y-3">
              <JsonBlock title="Error" value={row.error} />
              <JsonBlock title="Request" value={row.request} />
              <JsonBlock title="Response" value={row.response} />
              <JsonBlock title="Original body" value={row.body} />
              {!row.error && !row.request && !row.response && !row.body && <p className="text-[13px] text-ink-500">Nothing else was recorded for this entry.</p>}
            </div>
          </Section>
        </>
      )}
    </Drawer>
  );
};

/* -------------------------------------------------------------------- page */

const ActivityLogs = () => {
  const { logs } = useAppServices();
  const toast = useToast();

  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useOpenParam();

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: query.page, limit: query.limit });
    for (const key of ["appId", "kind", "result", "direction", "since", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await logs.Activity(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load the activity log", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.appId, query.kind, query.result, query.direction, query.since, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    logs.ActivityStats().then(({ response }) => setStats(response || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = Boolean(query.q || query.appId || query.kind || query.result || query.direction || query.since);
  const clearFilters = () => setQuery({ q: "", appId: "", kind: "", result: "", direction: "", since: "" });
  const closeDrawer = useCallback(() => setOpenId(null), [setOpenId]);
  const appImages = new Map((stats?.apps || []).map((a) => [a.appId, a.appImage]));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats ? (
          <>
            <Stat label="Last 24 hours" value={stats.today.toLocaleString()} hint="Messages and action steps" icon={<ScrollText size={16} />} tone="brand" />
            <Stat label="Failed today" value={stats.failedToday.toLocaleString()} hint="In the last 24 hours" icon={<XCircle size={16} />} tone="amber" />
            <Stat label="Failed this week" value={stats.failedWeek.toLocaleString()} hint="In the last 7 days" icon={<AlertTriangle size={16} />} tone="violet" />
            <Stat label="Warnings today" value={stats.warningsToday.toLocaleString()} hint="Expected, not errors" icon={<Timer size={16} />} tone="emerald" />
          </>
        ) : (
          [0, 1, 2, 3].map((k) => <Skeleton key={k} className="h-[7.5rem] w-full" rounded="rounded-2xl" />)
        )}
      </div>

      {stats?.byApp?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {stats.byApp.map((a) => {
            const on = query.appId === a.appId;
            return (
              <button
                key={a.appId}
                type="button"
                aria-pressed={on}
                onClick={() => setQuery({ appId: on ? "" : a.appId })}
                title="Last 7 days"
                className={cx(
                  "inline-flex items-center gap-2 rounded-xl border py-1.5 pl-1.5 pr-3 text-[12.5px] font-medium shadow-sm transition-colors",
                  on ? "border-brand-300 bg-brand-50 text-brand-800" : "border-ink-200 bg-white text-ink-700 hover:border-ink-300",
                )}
              >
                <AppIcon app={a} size="sm" className="!h-6 !w-6 !rounded-md !text-[9px]" />
                {a.appName}
                <span className={cx("tabular-nums", on ? "text-brand-600" : "text-ink-400")}>{a.total.toLocaleString()}</span>
                {a.failed > 0 && <span className="tabular-nums text-rose-500">· {a.failed.toLocaleString()} failed</span>}
              </button>
            );
          })}
        </div>
      )}

      <Card padded={false}>
        <ListFilters
          search={search}
          onSearch={setSearch}
          placeholder="Search location ID, company ID, action or message"
          filters={[
            { label: "App", value: query.appId, onChange: (appId) => setQuery({ appId }), options: [["", "All apps"], ...(stats?.apps || []).map((a) => [a.appId, a.appName])], className: "w-48" },
            { label: "Type", value: query.kind, onChange: (kind) => setQuery({ kind }), options: [["", "Any type"], ["conversation", "Messages"], ["action", "Workflow actions"]], className: "w-40" },
            { label: "Direction", value: query.direction, onChange: (direction) => setQuery({ direction }), options: [["", "Any direction"], ["outbound", "Outbound"], ["inbound", "Inbound"]], className: "w-36" },
            { label: "Result", value: query.result, onChange: (result) => setQuery({ result }), options: [["", "Any result"], ["ok", "OK"], ["failed", "Failed"], ["warning", "Warnings"]], className: "w-36" },
            { label: "Time", value: query.since, onChange: (since) => setQuery({ since }), options: [["", "Any time"], ["24h", "Last 24 hours"], ["7d", "Last 7 days"], ["30d", "Last 30 days"]], className: "w-36" },
          ]}
          onClear={clearFilters}
        />

        {loading && data.items.length === 0 ? (
          <div className="divide-y divide-ink-150">
            {[0, 1, 2, 3, 4].map((k) => (
              <div key={k} className="flex items-center gap-3 px-5 py-4">
                <Skeleton className="h-7 w-7" rounded="rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={<ScrollText size={24} />}
            title={filtered ? "Nothing matches" : "No activity yet"}
            description={filtered ? "Try another app, filter or search term." : "Messages and workflow steps appear here as they run."}
          />
        ) : (
          <div className={cx("overflow-x-auto transition-opacity", loading && "opacity-60")}>
            <table className="w-full min-w-[60rem] text-sm">
              <thead>
                <tr className="border-b border-ink-150 text-left">
                  {["When", "App", "What", "Account", "Result", "Message"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-150">
                {data.items.map((r) => (
                  <tr key={r._id} onClick={() => setOpenId(r._id)} className={cx("cursor-pointer align-top transition-colors hover:bg-ink-50", openId === r._id && "bg-brand-50/50")}>
                    <td className="whitespace-nowrap px-4 py-3 text-[12px] text-ink-500" title={stamp(r.createdAt)}>
                      {ago(r.createdAt)}
                    </td>
                    <td className="max-w-[12rem] px-4 py-3">
                      <div className="flex items-center gap-2">
                        <AppIcon app={{ appId: r.appId, appName: r.appName, appImage: appImages.get(r.appId) }} size="sm" className="!h-6 !w-6 !rounded-md !text-[9px]" />
                        <span className="truncate text-[12.5px] text-ink-700">{r.appName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-[12.5px] font-medium text-ink-800">{what(r)}</span>
                        <KindPill row={r} />
                      </div>
                    </td>
                    <td className="max-w-[12rem] px-4 py-3">
                      {r.accountName && <span className="block truncate text-[12.5px] text-ink-700">{r.accountName}</span>}
                      <span className="block truncate font-mono text-[11px] text-ink-400">{r.locationId || r.companyId || "—"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <ResultPill row={r} />
                        {r.status && <span className="font-mono text-[11px] text-ink-400">{r.status}</span>}
                      </div>
                    </td>
                    <td className="max-w-[22rem] px-4 py-3">
                      <p className={cx("line-clamp-2 text-[12px]", r.success ? "text-ink-500" : r.level === "warning" ? "text-amber-700" : "text-rose-600")}>{r.message || "—"}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!(loading && data.items.length === 0) && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "entry" : "entries"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </Card>

      {openId && <ActivityDrawer id={openId} onClose={closeDrawer} />}
    </div>
  );
};

export default ActivityLogs;
